/**
 * Native Authentication Utilities for Capacitor
 * Handles Google and Apple native sign-in via @capgo/capacitor-social-login,
 * then exchanges tokens with Supabase via signInWithIdToken.
 */

import { SocialLogin } from '@capgo/capacitor-social-login';
import { supabase } from './supabase';
import { platform } from './native';
import { logger } from './logger';

/**
 * Generate a cryptographic nonce and its SHA-256 hash.
 * Apple Sign in requires: raw nonce → Supabase, hashed nonce → Apple SDK.
 */
async function generateNonce() {
  const rawNonce = crypto.randomUUID();
  const encoder = new TextEncoder();
  const hashBuffer = await crypto.subtle.digest('SHA-256', encoder.encode(rawNonce));
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashedNonce = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  return { rawNonce, hashedNonce };
}

/**
 * Decode a JWT payload without verifying the signature.
 * Used only to inspect claims (nonce, aud) before sending to Supabase.
 * Returns the parsed payload object, or null on any decoding error.
 */
function decodeJwtPayload(token) {
  try {
    const base64Url = token.split('.')[1];
    if (!base64Url) return null;
    // Convert base64url to standard base64
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonStr = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + c.charCodeAt(0).toString(16).padStart(2, '0'))
        .join('')
    );
    return JSON.parse(jsonStr);
  } catch {
    return null;
  }
}

let initialized = false;

/**
 * Initialize the social login plugin.
 * Called lazily before first login attempt. Safe to call multiple times.
 */
async function ensureInitialized() {
  if (initialized || !platform.isNative) return;

  const config = {
    google: {
      iOSClientId: import.meta.env.VITE_GOOGLE_IOS_CLIENT_ID || '',
      iOSServerClientId: import.meta.env.VITE_GOOGLE_WEB_CLIENT_ID || '',
      webClientId: import.meta.env.VITE_GOOGLE_WEB_CLIENT_ID || '',
    },
  };

  // Apple native SDK only works on iOS (AuthenticationServices).
  // Android Apple login uses browser OAuth flow instead (handled in auth$.js).
  if (platform.isIOS) {
    config.apple = {};
  }

  await SocialLogin.initialize(config);
  initialized = true;
  logger.info('[NativeAuth] Social login initialized');
}

/**
 * Sign in with Google using the native SDK, then exchange for Supabase session.
 *
 * iOS GIDSignIn caches tokens for performance. If a cached token is returned,
 * its `nonce` claim won't match our freshly-generated hashedNonce, causing
 * Supabase to reject it with "nonces mismatch". We detect this by decoding the
 * JWT payload before calling Supabase, and if there's a mismatch we:
 *   1. Sign out from Google (clears GIDSignIn cache)
 *   2. Retry once — this forces Google to issue a fresh token with the new nonce
 * The retry generates its own fresh nonce pair so no state is shared across attempts.
 *
 * @param {boolean} [_retry=false] — internal flag, do not pass from outside
 * @returns {Promise<import('@supabase/supabase-js').AuthResponse>}
 */
export async function nativeGoogleLogin(_retry = false) {
  await ensureInitialized();
  const { rawNonce, hashedNonce } = await generateNonce();

  const result = await SocialLogin.login({
    provider: 'google',
    options: {
      scopes: ['email', 'profile'],
      nonce: hashedNonce,
    },
  });

  const idToken = result?.result?.idToken;
  if (!idToken) {
    throw new Error('Google sign-in did not return an ID token');
  }

  // Validate the nonce before sending to Supabase.
  // Google embeds the hashedNonce in the id_token's `nonce` claim.
  // If GIDSignIn returned a cached token, its nonce won't match ours.
  const payload = decodeJwtPayload(idToken);
  const tokenNonce = payload?.nonce;

  if (tokenNonce !== hashedNonce) {
    if (_retry) {
      // Second attempt still mismatched — give up to avoid infinite loop.
      logger.error('[NativeAuth] Google nonce mismatch on retry, giving up', { tokenNonce, hashedNonce });
      throw new Error('Google sign-in failed: nonce mismatch after retry. Please try again.');
    }

    // First attempt: cached token detected. Sign out from Google to clear
    // GIDSignIn's cache, then retry once with a fresh nonce.
    logger.warn('[NativeAuth] Google nonce mismatch (cached token detected), retrying after logout', {
      tokenNonce: tokenNonce ?? 'missing',
    });
    try {
      await SocialLogin.logout({ provider: 'google' });
    } catch (logoutErr) {
      // Best-effort: proceed even if logout fails
      logger.warn('[NativeAuth] Google logout before retry failed (non-fatal)', { error: logoutErr?.message });
    }
    return nativeGoogleLogin(true);
  }

  const { data, error } = await supabase.auth.signInWithIdToken({
    provider: 'google',
    token: idToken,
    nonce: rawNonce,
  });

  if (error) throw error;
  return data;
}

/**
 * Sign in with Apple using the native SDK, then exchange for Supabase session.
 * @returns {Promise<import('@supabase/supabase-js').AuthResponse>}
 */
export async function nativeAppleLogin() {
  await ensureInitialized();
  const { rawNonce, hashedNonce } = await generateNonce();

  let result;
  try {
    result = await SocialLogin.login({
      provider: 'apple',
      options: {
        scopes: ['email', 'name'],
        nonce: hashedNonce,
      },
    });
  } catch (pluginErr) {
    // ASAuthorizationError error 1000 = system-cancelled or blocked (no 2FA, child account, etc.)
    // Re-throw with a typed code so the UI can show actionable guidance instead of the raw iOS string.
    const msg = pluginErr?.message || '';
    if (msg.includes('AuthorizationError error 1000')) {
      const classified = new Error('Sign in with Apple was cancelled or unavailable.');
      classified.code = 'APPLE_SIGN_IN_ERROR_1000';
      classified.originalMessage = msg;
      throw classified;
    }
    throw pluginErr;
  }

  const idToken = result?.result?.idToken;
  if (!idToken) {
    throw new Error('Apple sign-in did not return an ID token');
  }

  const { data, error } = await supabase.auth.signInWithIdToken({
    provider: 'apple',
    token: idToken,
    nonce: rawNonce,
  });

  if (error) throw error;

  // Apple only provides the user's name on first sign-in, and it's not in the ID token.
  // Persist it to user metadata if available.
  const fullName = result?.result?.user?.name;
  if (fullName) {
    const displayName = [fullName.givenName, fullName.familyName].filter(Boolean).join(' ');
    if (displayName) {
      await supabase.auth.updateUser({ data: { full_name: displayName, username: displayName } }).catch(err => {
        logger.warn('[NativeAuth] Failed to persist Apple display name', { error: err.message });
      });
    }
  }

  return data;
}
